import {
  Button,
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  formatDate,
  formatSchoolYear,
  pn,
} from "@compass/core";
import { ListBeProperty } from "components";
import {
  LkStudentEnrollmentDisplayFieldId,
  Permission,
  PermissionAction,
  StudentEnrollmentListDto,
  StudentEnrollmentListFilter,
  StudentEnrollmentListResource,
} from "generated/openApi";
import { useList, useRouter } from "hooks";
import { studentEnrollmentPageRoute } from "pages/students/student-enrollments/StudentEnrollmentPage/StudentEnrollmentPage";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const Enrollments = (props: { initialLoad: boolean; studentId: number }) => {
  const ROOT_PERMISSION: Permission = Permission.StudentEnrollment;
  const navigate = useNavigate();

  const { matchTemplatePath } = useRouter();

  const [listFilter, setListFilter] = useState<StudentEnrollmentListFilter>({
    enrollmentFilter: { studentId: props.studentId },
    page: PAGE_DEFAULT,
    pageSize: PAGE_SIZE_UNLIMITED,
    sortObjects: [{ property: pn<StudentEnrollmentListDto>("schoolYear"), isDesc: true }],
  });
  const [listItems, setListItems] = useState<StudentEnrollmentListDto[]>([]);
  const [resource, setResource] = useState<StudentEnrollmentListResource>({});
  const properties = resource.properties || [];
  const displayFields = resource.displayFields || [];
  const startDateDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.StartDate);
  const schoolDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.School);
  const gradeLevelDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.GradeLevel);
  const endGradeLevelDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.EndGradeLevel);
  const participantStatusDisplayField = displayFields.find(
    (x) => x.id === LkStudentEnrollmentDisplayFieldId.ParticipantStatusId
  );

  const { tableContainerRef, loadList, applySort } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentEnrollments.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
  });

  useEffect(() => {
    if (props.initialLoad) {
      loadList(listFilter);

      ApiService.studentEnrollments.getListResource().then((response) => {
        setResource(response);
      });
    }
  }, [props.initialLoad]);

  return (
    <>
      <ListContainer
        fullHeight
        headerNoPadding
        header={
          <Stack
            direction="row"
            sx={{
              background: "#0d57a1",
              color: "info.main",
              px: 2,
              py: 1.2,
              fontSize: "20px",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Typography variant="inherit">Enrollments</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  variant="text"
                  onClick={() =>
                    navigate({
                      pathname: studentEnrollmentPageRoute.getPath({ id: 0, crudMode: "edit", screenType: "dialog" }),
                      search: studentEnrollmentPageRoute.getOptionalParams?.({
                        studentId: props.studentId,
                        hideStudent: true,
                      }),
                    })
                  }
                  sx={{ minWidth: "auto", color: "info.main" }}
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
        tableContainer={
          <TableContainer ref={tableContainerRef}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<StudentEnrollmentListDto>("schoolYear")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  {schoolDisplayField && (
                    <TableCell>
                      <ListBeProperty
                        listProperty={properties.find((x) => x.propertyName === pn<StudentEnrollmentListDto>("school"))}
                        displayName={schoolDisplayField?.displayName}
                        sortObjects={listFilter.sortObjects}
                        onChange={(sortObjects) => applySort(sortObjects)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<StudentEnrollmentListDto>("gradeLevel")
                      )}
                      displayName={gradeLevelDisplayField?.displayName}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  {endGradeLevelDisplayField && (
                    <TableCell>
                      <ListBeProperty
                        listProperty={properties.find((x) => x.propertyName === pn<StudentEnrollmentListDto>("endGradeLevel"))}
                        displayName={endGradeLevelDisplayField?.displayName}
                        sortObjects={listFilter.sortObjects}
                        onChange={(sortObjects) => applySort(sortObjects)}
                      />
                    </TableCell>
                  )}
                  {startDateDisplayField && (
                    <TableCell>
                      <ListBeProperty
                        listProperty={properties.find(
                          (x) => x.propertyName === pn<StudentEnrollmentListDto>("startDate")
                        )}
                        displayName={startDateDisplayField?.displayName}
                        sortObjects={listFilter.sortObjects}
                        onChange={(sortObjects) => applySort(sortObjects)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<StudentEnrollmentListDto>("endDate"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  {participantStatusDisplayField && (
                    <TableCell>
                      <ListBeProperty
                        listProperty={properties.find((x) => x.propertyName === pn<StudentEnrollmentListDto>("participantStatus"))}
                        sortObjects={listFilter.sortObjects}
                        onChange={(sortObjects) => applySort(sortObjects)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {listItems?.map((listItem) => (
                  <TableRow
                    key={listItem.id}
                    onClick={() =>
                      navigate({
                        pathname: studentEnrollmentPageRoute.getPath({
                          id: listItem.id,
                          crudMode: "view",
                          screenType: "dialog",
                        }),
                      })
                    }
                  >
                    <TableCell>{formatSchoolYear(listItem.schoolYear)}</TableCell>
                    {schoolDisplayField && <TableCell>{listItem.school}</TableCell>}
                    <TableCell>{listItem.gradeLevel}</TableCell>
                    {endGradeLevelDisplayField && <TableCell>{listItem.endGradeLevel}</TableCell>}
                    {startDateDisplayField && (
                      <TableCell>
                        {listItem.participantStatusId !== 99 && formatDate(listItem.startDate)}
                      </TableCell>
                    )}
                    <TableCell>{formatDate(listItem.endDate)}</TableCell>
                    {participantStatusDisplayField && (
                      <TableCell>{listItem.participantStatus}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
      />

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        {matchTemplatePath(studentEnrollmentPageRoute.templatePath) && <Outlet />}
      </ListContext.Provider>
    </>
  );
};
